// FILE: src/utils/nativeAttendance.js
//
// Handles GPS + Camera for BOTH website and Android APK.
// Capacitor.isNativePlatform() = true on APK, false on website.

import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';

/**
 * Gets GPS location with fake GPS detection.
 *
 * HOW FAKE GPS IS BLOCKED ON ANDROID APK:
 * ─────────────────────────────────────────
 * Android OS marks every location fix with Location.isMock = true
 * when any mock/fake GPS app is active (FakeGPS, GPS JoyStick, etc).
 * Capacitor's Geolocation plugin exposes this as coords.altitudeAccuracy === -1.
 * We read that flag and block the clock-in before it even reaches the server.
 *
 * On website (browser): isMock is always false — browser GPS cannot be
 * checked at OS level. We fall back to accuracy check instead.
 */
export const getNativeLocation = async () => {

  if (Capacitor.isNativePlatform()) {
    // ── ANDROID APK ──
    // Request permission properly — this fixes the "keep asking permission" issue
    const perm = await Geolocation.requestPermissions({ permissions: ['location'] });

    if (perm.location !== 'granted') {
      throw new Error('permission_denied: Location permission is required to clock in.');
    }

    const pos = await Geolocation.getCurrentPosition({
      enableHighAccuracy: true,
      timeout: 15000
    });

    // Android OS sets altitudeAccuracy = -1 when location is mocked
    const isMock = pos.coords.altitudeAccuracy === -1;

    return {
      latitude:      pos.coords.latitude,
      longitude:     pos.coords.longitude,
      accuracy:      pos.coords.accuracy,
      isMockLocation: isMock
    };

  } else {
    // ── WEBSITE / BROWSER ──
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        // Browser has no GPS — resolve with nulls, backend handles gracefully
        resolve({ latitude: null, longitude: null, accuracy: null, isMockLocation: false });
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({
          latitude:      pos.coords.latitude,
          longitude:     pos.coords.longitude,
          accuracy:      pos.coords.accuracy,
          isMockLocation: false   // cannot detect on browser
        }),
        () => resolve({ latitude: null, longitude: null, accuracy: null, isMockLocation: false }),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });
  }
};

/**
 * Opens camera and returns a JPEG blob.
 * On Android APK: native camera app opens (cannot be intercepted by fake apps).
 * On website: returns null — CameraModal (getUserMedia) handles it instead.
 */
export const getNativeSelfie = async () => {

  if (Capacitor.isNativePlatform()) {
    // ── ANDROID APK ──
    // Request camera permission properly
    const perm = await Camera.requestPermissions({ permissions: ['camera'] });

    if (perm.camera !== 'granted') {
      throw new Error('permission_denied: Camera permission is required to clock in.');
    }

    const photo = await Camera.getPhoto({
      quality:      85,
      allowEditing: false,
      resultType:   CameraResultType.DataUrl,
      source:       CameraSource.Camera,   // must be Camera, not Photos (gallery)
      direction:    'front',               // selfie / front camera
      saveToGallery: false                 // don't save to employee's photos
    });

    // Convert base64 data URL → Blob so FormData can append it
    const res = await fetch(photo.dataUrl);
    return await res.blob();

  } else {
    // ── WEBSITE ──
    // Return null — the CameraModal component handles getUserMedia on website
    return null;
  }
};
